import React from 'react';
import { ArrowLeft, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-rose-100">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-rose-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link to="/" className="inline-flex items-center text-gray-600 hover:text-rose-600 mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-rose-400 to-rose-600 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Privacy Policy</h1>
              <p className="text-sm text-gray-600">How we protect and handle your data</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        <Card className="p-6 sm:p-8 bg-white/80 backdrop-blur-sm border-rose-200">
          <div className="prose prose-gray max-w-none">
            <p className="text-gray-600 mb-8">
              <strong>Last Updated:</strong> {new Date().toLocaleDateString()}<br />
              <strong>Effective Date:</strong> September 20, 2025
            </p>

            <div className="bg-rose-50 border border-rose-200 rounded-lg p-6 mb-8">
              <h3 className="text-lg font-semibold text-rose-900 mb-3">
                🤖 Important: AI-Powered Service Notice
              </h3>
              <p className="text-rose-800">
                Constant Love features an AI companion named Conchi designed for self-love support. Responses are AI-generated, 
                not from human therapists. <strong>Constant Love is not a medical or therapy service.</strong> 
                If you're in crisis, please contact emergency services or crisis hotlines.
              </p>
            </div>

            <h2 className="text-xl font-bold text-gray-900 mb-4">1. Introduction</h2>
            <p className="mb-6">
              This Privacy Policy explains how Constant Love Co, LLC ("we," "us," "our," or "Constant Love") collects, uses, 
              and protects your personal information when you use our self-love and confidence-building application and services 
              (collectively, the "Service").
            </p>

            <h2 className="text-xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
            
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Account Information:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li><strong>Registration Data:</strong> Name, email address, password (encrypted), gender, birth date, language preference</li>
              <li><strong>Profile Information:</strong> Profile pictures, preferences, account settings</li>
              <li><strong>Communication Data:</strong> Messages and conversations with Conchi (our AI companion)</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">Wellness & Activity Data:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li><strong>Mood Tracking:</strong> Daily mood check-ins and emotional patterns</li>
              <li><strong>Journaling Content:</strong> Personal reflections, journal entries, and AI-generated insights</li>
              <li><strong>Affirmations:</strong> Daily affirmations received and saved to Love Vault</li>
              <li><strong>Progress Data:</strong> Love Score points, game achievements, streaks, milestones</li>
              <li><strong>Feature Usage:</strong> Vision Boards created, Divine Guidance readings, Love Garden game activity</li>
              <li><strong>Community Activity:</strong> Posts, comments, and interactions in community channels</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">Technical Data:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Device information (type, operating system, browser)</li>
              <li>IP address and location data (for timezone and content personalization)</li>
              <li>Usage analytics (features used, time spent, navigation patterns)</li>
              <li>Cookies and similar tracking technologies (Google Analytics, Facebook Pixel)</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">Payment Data:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li><strong>Payment Processing:</strong> Handled securely by Stripe (we never store credit card numbers)</li>
              <li><strong>Transaction Records:</strong> Subscription status, payment history, billing information</li>
              <li><strong>Retention:</strong> Transaction records kept for 7 years for legal and accounting purposes</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li><strong>Personalized AI Responses:</strong> Conchi uses your mood, journal entries, and conversation history to provide relevant, personalized self-love support</li>
              <li><strong>Feature Personalization:</strong> Daily affirmations, Divine Guidance, and content recommendations based on your preferences</li>
              <li><strong>Service Improvement:</strong> Analytics help us improve Conchi's responses, game features, and user experience</li>
              <li><strong>Account Management:</strong> Process payments, send important updates, and provide customer support</li>
              <li><strong>Safety & Moderation:</strong> Monitor for harmful content and ensure community guidelines compliance</li>
              <li><strong>Communications:</strong> Send service updates, feature announcements, and optional marketing communications</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-900 mb-4">4. AI Usage & Limitations</h2>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-blue-900 mb-3">🤖 AI Transparency:</h3>
              <ul className="space-y-2 text-blue-800">
                <li>• All Conchi responses are generated by artificial intelligence</li>
                <li>• Responses are not from licensed therapists, counselors, or medical professionals</li>
                <li>• AI may occasionally provide inaccurate or inappropriate responses</li>
                <li>• Conchi is designed for emotional support and self-love guidance, not clinical treatment</li>
                <li>• Users should seek professional help for serious mental health concerns</li>
                <li>• Your conversations may be reviewed for quality assurance and AI training (anonymized)</li>
              </ul>
            </div>

            <h2 className="text-xl font-bold text-gray-900 mb-4">5. Data Storage & Security</h2>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li><strong>Encryption:</strong> All personal data is encrypted in transit (TLS/SSL) and at rest</li>
              <li><strong>Storage Duration:</strong> Data is retained as long as your account is active</li>
              <li><strong>Secure Servers:</strong> Data is stored on industry-standard secure cloud infrastructure</li>
              <li><strong>Access Controls:</strong> Strict internal access controls protect your information</li>
              <li><strong>Regular Backups:</strong> Your data is backed up regularly to prevent loss</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-900 mb-4">6. Your Data Rights & Controls</h2>
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-green-900 mb-3">✅ You Have Complete Control:</h3>
              <ul className="space-y-2 text-green-800">
                <li>• <strong>View:</strong> Access all your data through Profile Settings > Privacy > "Your Data"</li>
                <li>• <strong>Export:</strong> Download your complete data in JSON format anytime</li>
                <li>• <strong>Delete Specific Data:</strong> Remove individual conversations, journal entries, or saved items</li>
                <li>• <strong>Reset Memory:</strong> Clear Conchi's memory while keeping your account</li>
                <li>• <strong>Account Deletion:</strong> Permanently delete your account and all associated data</li>
                <li>• <strong>Opt-Out:</strong> Unsubscribe from marketing emails (service emails still required)</li>
              </ul>
            </div>

            <p className="mb-6">
              Under GDPR, CCPA, and other privacy laws, you have rights to:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Access your personal information</li>
              <li>Correct inaccurate data</li>
              <li>Request data deletion</li>
              <li>Object to data processing</li>
              <li>Data portability</li>
              <li>Withdraw consent</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-900 mb-4">7. Third-Party Services</h2>
            <p className="mb-4">We use the following trusted third-party services:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li><strong>Stripe:</strong> Secure payment processing (handles payment data, not shared with us) - <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-rose-600 hover:underline">Privacy Policy</a></li>
              <li><strong>OpenAI GPT / Anthropic Claude:</strong> Powers Conchi's AI responses and affirmations</li>
              <li><strong>SendGrid:</strong> Email delivery service for transactional emails</li>
              <li><strong>MongoDB Atlas:</strong> Secure cloud database for data storage</li>
              <li><strong>Google Analytics:</strong> Website usage analytics (anonymized IP) - <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-rose-600 hover:underline">Privacy Policy</a></li>
              <li><strong>Facebook Pixel:</strong> Marketing campaign tracking (optional, can be opted out) - <a href="https://www.facebook.com/privacy/explanation" target="_blank" rel="noopener noreferrer" className="text-rose-600 hover:underline">Privacy Policy</a></li>
              <li><strong>GoHighLevel:</strong> Marketing affiliate tracking (optional program)</li>
            </ul>

            <p className="mb-6">
              These services have their own privacy policies and data protection measures. 
              We carefully vet all partners to ensure they meet high privacy standards.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mb-4">8. Data Sharing & Disclosure</h2>
            <p className="mb-4">
              <strong>We do NOT sell your personal data.</strong> We only share data in these limited circumstances:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li><strong>With Your Consent:</strong> When you explicitly authorize sharing</li>
              <li><strong>Service Providers:</strong> Third parties that help us operate the service (bound by confidentiality)</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or to protect rights and safety</li>
              <li><strong>Business Transfers:</strong> In event of merger, acquisition, or sale (users will be notified)</li>
              <li><strong>Anonymized Data:</strong> Aggregated, non-identifiable data for research and improvement</li>
            </ul>

            <h2 className="text-xl font-bold text-gray-900 mb-4">9. Sensitive Content & Crisis Support</h2>
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-red-900 mb-3">🚨 Critical Disclaimer:</h3>
              <ul className="space-y-2 text-red-800">
                <li>• <strong>Not Monitored:</strong> Constant Love Co, LLC does not actively monitor your conversations with Conchi</li>
                <li>• <strong>No Emergency Support:</strong> We do not provide 24/7 crisis intervention services</li>
                <li>• <strong>Crisis Detection:</strong> If you mention self-harm or crisis keywords, you'll be automatically shown emergency resources</li>
                <li>• <strong>Seek Professional Help:</strong> For mental health emergencies, contact 988 (Suicide & Crisis Lifeline) or emergency services</li>
                <li>• <strong>Privacy Exception:</strong> We may break confidentiality if we believe there is imminent danger to you or others</li>
              </ul>
            </div>

            <h2 className="text-xl font-bold text-gray-900 mb-4">10. Cookies & Tracking Technologies</h2>
            <p className="mb-4">We use cookies and similar technologies for:</p>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-amber-900 mb-3">🍪 Cookie Consent Required</h3>
              <p className="text-amber-800 mb-3">
                When you first visit our website, you'll see a cookie consent banner. You can accept or reject non-essential cookies.
              </p>
            </div>

            <ul className="list-disc pl-6 mb-6 space-y-3">
              <li>
                <strong>Essential Cookies:</strong> Required for login, authentication, and core functionality
                <br/>
                <span className="text-sm text-gray-600">Examples: auth_token, session_id (Always Active)</span>
              </li>
              <li>
                <strong>Preference Cookies:</strong> Remember your settings and preferences
                <br/>
                <span className="text-sm text-gray-600">Examples: language, theme, cookie_consent (Always Active)</span>
              </li>
              <li>
                <strong>Analytics Cookies (Google Analytics):</strong> Understand how users interact with the app
                <br/>
                <span className="text-sm text-gray-600">Purpose: Page views, user flows, feature usage (anonymized IP)</span>
                <br/>
                <span className="text-sm text-rose-600">Can be disabled via cookie banner</span>
              </li>
              <li>
                <strong>Marketing Cookies (Facebook Pixel):</strong> Track ad campaign effectiveness
                <br/>
                <span className="text-sm text-gray-600">Purpose: Conversion tracking, retargeting, audience building</span>
                <br/>
                <span className="text-sm text-rose-600">Can be disabled via cookie banner</span>
              </li>
            </ul>

            <p className="mb-4"><strong>How to Control Cookies:</strong></p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Click the cookie settings link in our banner (appears on first visit)</li>
              <li>Update preferences in your browser settings</li>
              <li>Use browser extensions to block trackers</li>
              <li>Opt out of Google Analytics: <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-rose-600 hover:underline">Browser Add-on</a></li>
              <li>Facebook opt-out: <a href="https://www.facebook.com/ads/preferences" target="_blank" rel="noopener noreferrer" className="text-rose-600 hover:underline">Ad Preferences</a></li>
            </ul>

            <p className="mb-6 text-sm text-gray-600">
              <strong>Note:</strong> Disabling essential cookies will prevent you from logging in and using core features. 
              Disabling analytics/marketing cookies will not affect your app experience.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mb-4">11. Children's Privacy</h2>
            <p className="mb-6">
              Constant Love is intended for users 18 years and older. We do not knowingly collect personal information 
              from children under 18. If you're under 18, please obtain parental or guardian consent before using our service. 
              If we discover we've collected data from a child under 18, we will delete it immediately.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mb-4">12. GDPR Compliance (European Users)</h2>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-blue-900 mb-3">🇪🇺 Your GDPR Rights</h3>
              <p className="text-blue-800 mb-3">
                If you're in the European Economic Area (EEA), UK, or Switzerland, you have additional rights under GDPR:
              </p>
              <ul className="space-y-2 text-blue-800">
                <li>• <strong>Right to Access:</strong> Request a copy of all your personal data (we'll provide within 30 days)</li>
                <li>• <strong>Right to Rectification:</strong> Correct any inaccurate or incomplete data</li>
                <li>• <strong>Right to Erasure:</strong> Request deletion of your data ("Right to be Forgotten")</li>
                <li>• <strong>Right to Restriction:</strong> Limit how we process your data</li>
                <li>• <strong>Right to Data Portability:</strong> Receive your data in machine-readable format (JSON)</li>
                <li>• <strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
                <li>• <strong>Right to Withdraw Consent:</strong> Withdraw consent anytime (doesn't affect prior processing)</li>
                <li>• <strong>Right to Lodge Complaint:</strong> File complaint with your local Data Protection Authority</li>
              </ul>
            </div>

            <p className="mb-4"><strong>How to Exercise Your Rights:</strong></p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Email: privacy@constantloveco.com with subject "GDPR Request"</li>
              <li>In-app: Settings → Privacy → "Your Data Rights"</li>
              <li>We'll respond within 30 days (extendable to 60 days for complex requests)</li>
              <li>We may ask for ID verification to protect your privacy</li>
            </ul>

            <p className="mb-4"><strong>Legal Basis for Processing (GDPR Article 6):</strong></p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li><strong>Contract Performance:</strong> To provide services you signed up for</li>
              <li><strong>Consent:</strong> For marketing emails, analytics cookies, Facebook Pixel</li>
              <li><strong>Legitimate Interests:</strong> Fraud prevention, service improvement, security</li>
              <li><strong>Legal Obligation:</strong> Tax compliance, legal requests, safety concerns</li>
            </ul>

            <p className="mb-4"><strong>Data Retention:</strong></p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li><strong>Active Accounts:</strong> Data retained while your account is active</li>
              <li><strong>Deleted Accounts:</strong> 30-day soft delete (recoverable), then permanent deletion</li>
              <li><strong>Anonymized Data:</strong> We may retain anonymized analytics indefinitely</li>
              <li><strong>Legal Requirements:</strong> Transaction records kept for 7 years (accounting/tax law)</li>
              <li><strong>Backups:</strong> Deleted data removed from backups within 90 days</li>
            </ul>

            <p className="mb-6">
              <strong>EU Representative:</strong> For GDPR-related inquiries, contact privacy@constantloveco.com. 
              We commit to addressing all GDPR requests in compliance with EU regulations.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mb-4">13. International Data Transfers</h2>
            <p className="mb-6">
              Constant Love Co, LLC is based in the United States. If you're accessing our service from outside the US, 
              your data may be transferred to and processed in the United States. We implement appropriate safeguards 
              to protect your data in compliance with applicable laws, including GDPR Standard Contractual Clauses where required.
            </p>

            <p className="mb-6">
              <strong>Transfer Mechanisms:</strong> We use Standard Contractual Clauses (SCCs) approved by the European Commission 
              for data transfers from the EU to the US. Our cloud providers (MongoDB Atlas, AWS) comply with EU-US Data Privacy Framework.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mb-4">14. Changes to This Policy</h2>
            <p className="mb-6">
              We may update this Privacy Policy periodically to reflect changes in our practices, features, or legal requirements. 
              We'll notify you of significant changes via:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Email notification to your registered address</li>
              <li>In-app notification</li>
              <li>Notice on our website</li>
            </ul>
            <p className="mb-6">
              Your continued use of Constant Love after changes constitutes acceptance of the updated policy.
            </p>

            <h2 className="text-xl font-bold text-gray-900 mb-4">15. Contact Us</h2>
            <p className="mb-4">
              Questions, concerns, or requests regarding your privacy or this policy? We're here to help:
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-8">
              <p><strong>Constant Love Co, LLC</strong></p>
              <p className="mt-2">Email: privacy@constantloveco.com</p>
              <p>Support: support@constantloveco.com</p>
              <p>Help Center: Available in the Constant Love app</p>
              <p className="mt-2">Response time: Within 48 hours for privacy requests</p>
            </div>

            <div className="border-t pt-6 mt-8">
              <p className="text-sm text-gray-600">
                This privacy policy is part of our commitment to transparency and your data protection rights 
                under GDPR, CCPA, and other applicable privacy laws. Constant Love Co, LLC is committed to 
                protecting your privacy and handling your data with care and respect.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;